import React, { useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';

/*
 * Homepage FAQ.
 *
 * TWO CONSTRAINTS, AND THEY PULL AGAINST EACH OTHER:
 *
 * 1. Twelve full-width cards ran to several screens of scroll and buried everything below
 *    it. So only the first five sit in the flow and the rest are behind one control.
 *    The cards keep their original size — the saving is in how many are shown, not in
 *    shrinking them.
 *
 * 2. The FAQPage schema below declares all twelve questions, so all twelve answers must
 *    exist in the DOM. Schema that describes content the page does not contain is a
 *    structured-data violation, not a clever trick.
 *
 * How both are satisfied: every question and answer is ALWAYS RENDERED. Nothing is
 * conditionally mounted. Collapsing is done with native <details> (the answer stays in the
 * DOM when closed) and the overflow group is hidden with CSS, not unmounted. Content behind
 * a user-operable expander is explicitly fine for Google; content that only exists after a
 * click is not — which is what the previous AnimatePresence version did, unmounting every
 * closed answer and leaving the schema describing text that was not on the page.
 *
 * If you refactor this, the rule is: never gate a question or answer behind `&&`.
 */

const faqs = [
  {
    question: 'What is blockchain security audit?',
    answer:
      'A blockchain security audit is a comprehensive review of smart contracts, protocols, and blockchain applications to identify vulnerabilities, security flaws, and potential exploits before deployment. Safe Edges conducts thorough audits using manual code review, automated testing, fuzzing, and exploit-path simulation to ensure your project is secure.',
  },
  {
    question: 'How much does a smart contract audit cost?',
    answer:
      "Smart contract audit costs vary based on code complexity, project size, and audit depth. Safe Edges offers competitive pricing starting from affordable rates while maintaining top-tier security standards. Contact us at info@safeedges.in for a customized quote tailored to your project's specific needs.",
  },
  {
    question: 'How long does a smart contract audit take?',
    answer:
      'Audit duration depends on codebase size and complexity. Typically, a thorough smart contract audit takes 1-4 weeks. Safe Edges works efficiently without compromising quality, providing detailed reports with prioritized remediation guidance to ensure your project launches securely.',
  },
  {
    question: 'What chains do you audit?',
    answer:
      'Safe Edges audits smart contracts across 24+ blockchain networks including Ethereum, Solana, Base, Arbitrum, Sui, Starknet, Polygon, zkSync, BSC, Near, Cosmos, Flow, Xion, and more. We have deep expertise in EVM-compatible chains, Rust-based blockchains, Move language chains, and specialized Layer 2 solutions.',
  },
  {
    question: 'What makes Safe Edges different from other audit firms?',
    answer:
      'Safe Edges maintains a 100% security track record with zero hacks on audited projects. We are official security partners of Fuel Chain, Berachain, and 200+ trusted projects. Our team includes ex-CISO experts and top-ranked auditors from HackenProof, offering top-tier security at affordable prices.',
  },
  {
    question: 'What is cybersecurity audit for blockchain?',
    answer:
      'A cybersecurity audit for blockchain involves comprehensive security assessment of smart contracts, DeFi protocols, wallets, dApps, and blockchain infrastructure. Safe Edges provides end-to-end cybersecurity audits covering code security, economic vulnerabilities, access controls, and operational security to protect digital assets.',
  },
  {
    question: 'What is DeFi security audit?',
    answer:
      'DeFi security audit is a specialized security review of decentralized finance protocols covering smart contracts, economic models, oracle integrations, governance mechanisms, and liquidity mechanics. Safe Edges conducts comprehensive DeFi audits to identify vulnerabilities in lending, AMMs, yield farming, and other DeFi primitives.',
  },
  {
    question: 'Do you provide post-audit support?',
    answer:
      'Yes, Safe Edges provides comprehensive post-audit support including remediation guidance, retesting, deployment readiness assessment, and ongoing security monitoring. We ensure your project is production-ready and help maintain security standards after launch.',
  },
  {
    question: 'What programming languages do you audit?',
    answer:
      'Safe Edges audits smart contracts written in Solidity, Rust, Move, Cairo, Clarity, Sway, and other blockchain-specific languages. We have deep expertise across EVM chains, Solana, Sui/Aptos, Starknet, Stacks, Fuel, and other blockchain ecosystems.',
  },
  {
    question: 'Can you audit AI and MCP implementations?',
    answer:
      'Yes, Safe Edges specializes in AI & MCP (Model Context Protocol) audits, reviewing AI agent implementations, tool access controls, prompt safety, on-chain execution security, and AI-integrated DeFi protocols. We ensure AI systems interacting with blockchain are secure and reliable.',
  },
  {
    question: 'Why choose Safe Edges for blockchain security audit?',
    answer:
      "Safe Edges maintains a 100% security track record with zero hacks on audited projects. We are official security partners of Fuel Chain, Berachain, and 200+ trusted projects. Our team includes ex-CISO experts and top-ranked auditors from HackenProof, offering top-tier security at affordable prices. We've secured over $4B+ in digital assets and provide comprehensive post-audit support.",
  },
  {
    question: 'What expertise do Safe Edges auditors have?',
    answer:
      'Our auditors are world-class security experts with proven track records. Our team includes Top 3 Global Rank holders on HackenProof, ex-CISO professionals, ex-Polygon blockchain security experts, and researchers who have secured over $4B in blockchain assets. We have deep expertise across 24+ blockchain networks, multiple CVE discoveries, and extensive experience in smart contract auditing, DeFi security, and zero-day research.',
  },
];

/* How many sit in the page flow before the "Show all" control. The rest stay in the DOM. */
const VISIBLE_COUNT = 5;

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
};

/* Native <details>: the answer is in the DOM whether the row is open or shut, the browser
 * handles the toggle, and it stays keyboard-accessible without any state of our own. */
const FaqItem = ({ question, answer }) => (
  <details className="group border-b border-white/[0.08]">
    <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-6 [&::-webkit-details-marker]:hidden">
      <h3 className="text-base font-medium leading-snug tracking-[-0.01em] text-[#e6ebf1] transition-colors duration-200 group-hover:text-teal-300 group-open:text-white md:text-lg">
        {question}
      </h3>
      <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full ring-1 ring-white/10 transition-colors duration-200 group-hover:ring-teal-400/40 group-open:bg-white/[0.06]">
        <HiChevronDown className="h-4 w-4 text-[#8792a2] transition-transform duration-300 group-open:rotate-180 group-open:text-teal-300" />
      </span>
    </summary>
    <p className="max-w-[62ch] pb-6 pr-12 text-[15px] leading-relaxed text-[#a3acb9]">{answer}</p>
  </details>
);

const FAQSection = () => {
  const [showAll, setShowAll] = useState(false);
  const hiddenCount = faqs.length - VISIBLE_COUNT;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <section className="relative bg-black py-24 md:py-32 overflow-hidden border-t border-white/[0.06]">
        <div className="relative z-10 mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)] lg:gap-20">
          {/* Left: heading + contact, sticks while the list scrolls */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <h2 className="mb-5 text-4xl font-semibold leading-[1.08] tracking-[-0.03em] md:text-5xl">
              <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-teal-400 bg-clip-text text-transparent">
                Frequently Asked Questions
              </span>
            </h2>
            <p className="text-lg leading-relaxed text-[#a3acb9]">
              Everything you need to know about blockchain security audits, smart contract
              auditing, and cybersecurity services
            </p>

            <div className="mt-10">
              <p className="mb-5 text-[15px] text-[#8792a2]">
                Still have questions? Contact our security experts
              </p>
              <a
                href="/contact"
                className="group inline-flex items-center gap-1.5 rounded-full bg-[#00A99E] px-5 py-2.5 text-sm font-semibold text-black transition-colors duration-200 hover:bg-[#2ee6d6]"
              >
                Contact Us
                <svg className="transition-transform duration-200 group-hover:translate-x-0.5" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: divided question list */}
          <div className="border-t border-white/[0.08]">
            {faqs.slice(0, VISIBLE_COUNT).map((faq) => (
              <FaqItem key={faq.question} {...faq} />
            ))}

            {/* Rendered unconditionally; `hidden` only changes CSS display, so the answers
                stay in the DOM for crawlers and the schema above stays honest. */}
            <div hidden={!showAll}>
              {faqs.slice(VISIBLE_COUNT).map((faq) => (
                <FaqItem key={faq.question} {...faq} />
              ))}
            </div>

            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
              className="mt-8 inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold text-[#e6ebf1] ring-1 ring-white/15 transition-colors duration-200 hover:bg-white/[0.06] hover:ring-white/30"
            >
              {showAll ? 'Show fewer questions' : `Show ${hiddenCount} more questions`}
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQSection;
